import { useEffect, useState } from 'react';
import { supabase } from '@shared/lib/supabase';

interface LogEntry {
  id: string;
  admin_email: string;
  action: string;
  target_type: string;
  target_id: string;
  details: Record<string, unknown>;
  created_at: string;
}

const ACTION_COLORS: Record<string, string> = {
  approve_application: 'text-green-400 bg-green-400/10 border-green-400/20',
  reject_application: 'text-red-400 bg-red-400/10 border-red-400/20',
  rotate_api_key: 'text-amber-400 bg-amber-400/10 border-amber-400/20',
  update_settings: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
};

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

interface Props {
  limit?: number;
  compact?: boolean;
}

export default function AdminAuditLog({ limit = 50, compact = false }: Props) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('admin_audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (data) setLogs(data as LogEntry[]);
      setLoading(false);
    }
    load();
  }, [limit]);

  if (loading) {
    return <p className="font-mono text-sm py-4" style={{ color: 'var(--dash-text-dim)' }}>Loading audit log...</p>;
  }

  if (logs.length === 0) {
    return (
      <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No audit log entries yet.</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
        {logs.map((log, i) => (
          <div
            key={log.id}
            className="px-4 py-3 flex items-center justify-between"
            style={{
              background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
              borderBottom: i < logs.length - 1 ? '1px solid var(--dash-border-light)' : undefined,
            }}
          >
            <div className="flex items-center gap-3">
              <span
                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                  ACTION_COLORS[log.action] || 'text-zinc-400 bg-zinc-800/30 border-zinc-700/30'
                }`}
              >
                {log.action.replace(/_/g, ' ')}
              </span>
              <span className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                {log.target_type} {log.target_id.slice(0, 8)}...
              </span>
            </div>
            <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>{timeAgo(log.created_at)}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
      <table className="w-full text-sm font-sans">
        <thead>
          <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Action</th>
            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Target</th>
            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Admin</th>
            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Time</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log, i) => (
            <tr
              key={log.id}
              className="transition-colors"
              style={{
                background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                borderBottom: '1px solid var(--dash-border-light)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
            >
              <td className="px-4 py-3">
                <span
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                    ACTION_COLORS[log.action] || 'text-zinc-400 bg-zinc-800/30 border-zinc-700/30'
                  }`}
                >
                  {log.action.replace(/_/g, ' ')}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{log.target_type}</div>
                <div className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>{log.target_id.slice(0, 12)}...</div>
              </td>
              <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{log.admin_email}</td>
              <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>{timeAgo(log.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
